import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useStyles = makeStyles({
  filterBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[2]} 0`,
    flexWrap: "nowrap",
    flexShrink: 0,
    maxWidth: "100%",
    overflowX: "auto",
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    "@media (max-width: 640px)": {
      gap: designTokens.spacing[2],
    },
  },

  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
  },

  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    flexWrap: "nowrap",
    flexGrow: 1,
    "@media (max-width: 640px)": {
      flexWrap: "wrap",
    },
  },

  countContainer: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    fontSize: designTokens.fontSize.sm,
    fontFamily: designTokens.fontFamily,
    color: designTokens.neutral[900],
    whiteSpace: "nowrap",
    minWidth: "70px",
    "@media (max-width: 640px)": {
      display: "none",
    },
  },

  countBold: {
    fontWeight: designTokens.fontWeight.bold,
    color: designTokens.neutral[900],
    fontSize: designTokens.fontSize.sm,
  },

  countLabel: {
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[600],
    fontSize: designTokens.fontSize.sm,
  },

  countLoading: {
    animationName: {
      from: { opacity: 0.6 },
      to: { opacity: 1 },
    },
    animationDuration: "300ms",
    animationIterationCount: "infinite",
    animationDirection: "alternate",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
  },

  pillButton: {
    borderRadius: designTokens.borderRadius.lg,
  },

  workspaceGroup: {
    display: "inline-flex",
    alignItems: "center",
    padding: `${designTokens.spacing[1]}`,
    backgroundColor: designTokens.accent[50],
    border: `1px solid ${designTokens.accent[500]}`,
    borderRadius: designTokens.borderRadius.lg,
    flexShrink: 0,
  },

  workspaceGroupDivider: {
    height: designTokens.spacing[4],
    flexShrink: 0,
    backgroundColor: designTokens.accent[500],
  },

  workspaceIconInner: {
    fontSize: designTokens.fontSize.xl,
    color: designTokens.accent[500],
    flexShrink: 0,
  },

  workspaceButton: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    padding: `${designTokens.spacing[1]} 0 ${designTokens.spacing[1]} ${designTokens.spacing[1]}`,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.accent[500],
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    fontFamily: designTokens.fontFamily,
    minHeight: "28px",
    height: "28px",
    width: "225px",
    overflow: "hidden",

    ":hover:not(:disabled)": {
      backgroundColor: designTokens.accent[50],
    },

    ":active:not(:disabled)": {
      backgroundColor: designTokens.accent[100],
    },

    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },

    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },

    "@media (max-width: 640px)": {
      minHeight: "44px",
      height: "44px",
      padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
      gap: designTokens.spacing[1],
    },
  },

  verticalDivider: {
    height: designTokens.spacing[8],
    flexShrink: 0,
    margin: `0 ${designTokens.spacing[2]}`,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },

  icon: {
    fontSize: designTokens.fontSize.xl,
    color: designTokens.neutral[600],
  },

  filterIcon: {
    fontSize: designTokens.fontSize.xl,
    color: "currentColor",
  },

  label: {
    fontFamily: designTokens.fontFamily,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },

  workspaceLabel: {
    fontFamily: designTokens.fontFamily,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left" as const,
    flex: 1,
    minWidth: 0,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },

  chevron: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.accent[500],
    transition: `transform ${designTokens.transition.fast}`,
    marginLeft: "auto",
    flexShrink: 0,
  },

  chevronOpen: {
    transform: "rotate(180deg)",
  },

  popoverSurface: {
    backgroundColor: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: designTokens.shadow.lg,
    padding: "0",
    border: "none",
    minWidth: "220px",
    maxWidth: "300px",
    overflow: "hidden",
    "@media (max-width: 640px)": {
      minWidth: "min(280px, calc(100vw - 48px))",
      maxWidth: "calc(100vw - 48px)",
    },
  },

  menu: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    padding: "0",
    maxHeight: "280px",
    overflowY: "auto",
  },

  menuItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: designTokens.spacing[2],
    height: designTokens.controlHeight.md,
    paddingLeft: designTokens.spacing[4],
    paddingRight: designTokens.spacing[4],
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    fontFamily: designTokens.fontFamily,
    transition: `all ${designTokens.transition.fast}`,
    textAlign: "left",

    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },

    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "-2px",
    },
  },

  menuItemSelected: {
    backgroundColor: designTokens.neutral[50],

    ":hover": {
      backgroundColor: designTokens.neutral[100],
    },
  },

  menuItemIcon: {
    fontSize: designTokens.iconSize.sm,
    color: designTokens.neutral[400],
    flexShrink: 0,
  },

  menuItemLabel: {
    flex: 1,
    fontFamily: designTokens.fontFamily,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
  },

  checkmark: {
    fontSize: designTokens.iconSize.sm,
    color: designTokens.accent[500],
    flexShrink: 0,
  },

  sectionDivider: {
    height: "1px",
    backgroundColor: designTokens.neutral[200],
    margin: `${designTokens.spacing[2]} 0`,
  },

  newChatButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[1],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    backgroundColor: designTokens.accent[500],
    border: "none",
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.medium,
    color: designTokens.neutral.white,
    cursor: "pointer",
    transition: `background-color ${designTokens.transition.fast}`,
    whiteSpace: "nowrap",
    fontFamily: designTokens.fontFamily,
    minHeight: "28px",
    height: "28px",

    ":hover:not(:disabled)": {
      backgroundColor: designTokens.accent[600],
    },

    ":active:not(:disabled)": {
      backgroundColor: designTokens.accent[700],
    },

    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },

    "@media (max-width: 640px)": {
      minHeight: "32px",
      height: "32px",
      padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
      gap: designTokens.spacing[1],
    },
  },

  newChatLabel: {
    fontFamily: designTokens.fontFamily,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },

  filterBarWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flexShrink: 0,
  },

  sortGroupRight: {
    marginLeft: "auto",
  },

  hintText: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    fontFamily: designTokens.fontFamily,
    fontStyle: "italic",
    paddingLeft: designTokens.spacing[1],
  },
});
